// src/components/Jobs/JobSearchBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { getSearchSuggestions } from '../../utils/search';

const JobSearchBar = ({ onSearch, jobs, placeholder = "Search jobs, skills, companies..." }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        
        if (value.length >= 2) {
            const suggestions = getSearchSuggestions(jobs, value);
            setSuggestions(suggestions);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSearch(query);
        setShowSuggestions(false);
    };

    const handleSuggestionClick = (suggestion) => {
        setQuery(suggestion);
        onSearch(suggestion);
        setShowSuggestions(false);
    };

    const clearSearch = () => {
        setQuery('');
        onSearch('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    return (
        <div ref={wrapperRef} className="relative w-full">
            <form onSubmit={handleSubmit} className="relative">
                <div className="relative">
                    <input
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                        placeholder={placeholder}
                        className="w-full p-3 pl-12 pr-12 border border-gray-300 rounded-xl focus:border-gradpath-green focus:ring-2 focus:ring-gradpath-green/20 outline-none transition-all"
                    />
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    {query && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        >
                            <i className="fas fa-times-circle"></i>
                        </button>
                    )}
                </div>
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
                    <ul className="py-2">
                        {suggestions.map((suggestion, index) => (
                            <li
                                key={index}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3 transition"
                            >
                                <i className="fas fa-search text-gray-400 text-sm"></i>
                                <span className="text-gray-700">{suggestion}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Search Tips */}
            {isFocused && !showSuggestions && query.length > 0 && suggestions.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-50">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="fas fa-lightbulb text-yellow-500"></i>
                        <span>Try using different keywords or check your spelling</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobSearchBar;